import { apiClient, getApiBaseUrl } from '../config/api';

// FEAT-047 admin client (MVP-8.19, MVP-8.35, MVP-18.41).
// Full-field admin variant of the state agency mappings API. Public
// surfaces never see `verifyUrlTemplate` or `notes`; the admin variant
// does because admins curate them.

export interface AdminStateAgencyMapping {
  stateCode: string;
  agencyLabel: string;
  agencyFullName: string;
  registerUrl: string;
  verifyUrlTemplate: string;
  notes: string | null;
  active: boolean;
  updatedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertStateAgencyMappingInput {
  stateCode: string;
  agencyLabel: string;
  agencyFullName: string;
  registerUrl: string;
  verifyUrlTemplate: string;
  notes?: string;
  active?: boolean;
}

export interface BulkImportSummary {
  created: number;
  updated: number;
  skipped: number;
  errors: Array<{ line: number; message: string }>;
}

class StateAgencyMappingsAdminService {
  async list(): Promise<AdminStateAgencyMapping[]> {
    const response = await apiClient.get<{ mappings: AdminStateAgencyMapping[] }>(
      '/admin/state-agency-mappings',
    );
    return response.data.mappings ?? [];
  }

  async upsert(input: UpsertStateAgencyMappingInput): Promise<AdminStateAgencyMapping> {
    const response = await apiClient.post<{ mapping: AdminStateAgencyMapping }>(
      '/admin/state-agency-mappings',
      input,
    );
    return response.data.mapping;
  }

  async deactivate(stateCode: string): Promise<AdminStateAgencyMapping> {
    const response = await apiClient.delete<{ mapping: AdminStateAgencyMapping }>(
      `/admin/state-agency-mappings/${encodeURIComponent(stateCode)}`,
    );
    return response.data.mapping;
  }

  async bulkImportCsv(file: File): Promise<BulkImportSummary> {
    const baseUrl = await getApiBaseUrl();
    const token = localStorage.getItem('authToken');
    const formData = new FormData();
    formData.append('csv', file);

    const response = await fetch(`${baseUrl}/admin/state-agency-mappings/bulk-import`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      body: formData,
    });
    const data = await response.json();
    if (!response.ok) {
      const message = (data?.error?.message as string | undefined) ?? `CSV import failed: ${response.status}`;
      throw new Error(message);
    }
    return data as BulkImportSummary;
  }
}

export const stateAgencyMappingsAdminService = new StateAgencyMappingsAdminService();
